import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { SCHEDULER_ACCESSES, SCHEDULER_MODULE_KEY } from '../constants/scheduler-access';
import { CustomNotificationService } from './custom.notification.service';
import { OrgModulesService } from './org-modules.service';
import { PermissionService } from './permissions.service';

export type SchedulerDenial = 'module' | 'access';

/**
 * Regla única de visibilidad de «Programaciones», compartida por este guard y la entrada del menú
 * (`PagesComponent.canAccessScheduler`): qué falta para entrar (el módulo `scheduler` en `orgModules`, algún access
 * `scheduler_admin` / `scheduler_read`) o `null` si no falta nada.
 */
export function schedulerDenial(
  orgModules: Pick<OrgModulesService, 'hasAccessToModule'>,
  permissions: Pick<PermissionService, 'hasAnyPermission'>,
): SchedulerDenial | null {
  if (!orgModules.hasAccessToModule(SCHEDULER_MODULE_KEY)) return 'module';
  if (!permissions.hasAnyPermission([...SCHEDULER_ACCESSES])) return 'access';
  return null;
}

/** Texto del aviso «Sin acceso» según lo que falte (mismo patrón que `AgentsEngineModuleGuard`). */
export const SCHEDULER_DENIAL_MESSAGES: Readonly<Record<SchedulerDenial, string>> = {
  module: 'Tu organización no tiene habilitado el módulo de Programaciones.',
  access:
    'Tu usuario no tiene ningún acceso de Programaciones (scheduler_admin o scheduler_read). Pide a un administrador de tu organización que te asigne alguno y vuelve a entrar.',
};

/**
 * Protege `/pages/scheduler` (feature nativa «Programaciones»): la organización debe tener habilitado el módulo
 * `scheduler` en `org_modules` (viene en el JWT como `orgModules`) y el usuario alguno de los accesos
 * `scheduler_admin` / `scheduler_read` (misma condición que la entrada del menú). Sin ello: aviso «Sin acceso» y
 * vuelta al dashboard. Dentro de la feature, las acciones de escritura se deshabilitan sin `scheduler_admin`; la
 * autoridad es la API.
 */
@Injectable({
  providedIn: 'root',
})
export class SchedulerModuleGuard implements CanActivate {
  constructor(
    private readonly orgModulesService: OrgModulesService,
    private readonly permissionService: PermissionService,
    private readonly router: Router,
    private readonly notification: CustomNotificationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const denial = schedulerDenial(this.orgModulesService, this.permissionService);
    if (!denial) {
      return true;
    }

    this.notification.show('warning', 'Sin acceso', SCHEDULER_DENIAL_MESSAGES[denial], 'topRight');
    this.router.navigate(['/pages/dashboard'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }
}
